/**
 * CardanoMonitor Page
 *
 * Monitor Cardano accounts, staking to pools, native assets, and transactions.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useCardanoWallets } from '../hooks/useCardanoWallet';
import { useCardanoMonitorWallets } from '../hooks/useMonitorWallets';
import { isValidCardanoAddress, cardanoAPI } from '../services/cardanoApi';
import { useToast } from '../components/Toast';
import { CardanoHealthMetrics } from '../components/cardano/CardanoHealthMetrics';
import { AdaSupplyBreakdown } from '../components/cardano/AdaSupplyBreakdown';
import {
  NetworkMonitorPageHeader,
  NetworkAddressInputForm,
  NetworkMonitorEmptyState,
  MonitorTabs,
} from '../components/monitor';
import { ResponsiveTable } from '../components/ui/ResponsiveTable';
import type { CardanoWalletData } from '../types/cardano.types';

const ADA_COLORS = {
  primary: '#0033AD',
  secondary: '#1A44B8',
  accent: '#3366FF',
  gradient: 'linear-gradient(135deg, #0033AD, #1A44B8)',
  lightGradient: 'linear-gradient(135deg, rgba(0, 51, 173, 0.15), rgba(26, 68, 184, 0.1))',
  success: '#10b981',
  warning: '#f59e0b',
};

type TabType = 'overview' | 'transactions' | 'assets' | 'staking';

const s = {
  page: { padding: '0', maxWidth: '1400px', margin: '0 auto', paddingBottom: '40px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', gap: '20px', flexWrap: 'wrap' as const },
  title: { display: 'flex', alignItems: 'center', gap: '12px' },
  titleText: { fontSize: '24px', fontWeight: 700, margin: 0 },
  subtitle: { fontSize: '13px', color: 'var(--muted)', marginTop: '4px' },
  card: { background: 'rgba(0, 0, 0, 0.25)', backdropFilter: 'blur(20px)', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.08)', padding: '20px', marginBottom: '20px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: (color: string) => ({ background: `${color}10`, border: `1px solid ${color}30`, borderRadius: '12px', padding: '16px', textAlign: 'center' as const }),
  statValue: { fontSize: '22px', fontWeight: 700 },
  statLabel: { fontSize: '11px', color: 'var(--muted)', marginTop: '4px', textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  input: { width: '100%', padding: '12px 14px', background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '10px', color: 'var(--text)', fontSize: '13px', fontFamily: 'monospace', outline: 'none' },
  button: { padding: '12px 20px', background: ADA_COLORS.gradient, color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' },
  buttonSecondary: { padding: '12px 20px', background: 'rgba(255, 255, 255, 0.1)', color: 'var(--text)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
  tabContainer: { display: 'flex', gap: '4px', padding: '4px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '14px', width: 'fit-content', marginBottom: '24px' },
  tab: (isActive: boolean) => ({ padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.3s ease', background: isActive ? ADA_COLORS.gradient : 'transparent', color: isActive ? 'white' : 'var(--muted)' }),
  emptyState: { textAlign: 'center' as const, padding: '60px 20px' },
};

function NetworkStatsBanner({ adaPrice, totalAccounts, isLoading }: { adaPrice: number; totalAccounts: number; isLoading: boolean }) {
  const { t } = useTranslation();
  return (
    <div style={{ ...s.card, background: ADA_COLORS.lightGradient, border: '1px solid rgba(0, 51, 173, 0.25)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>{t('cardano.network.title', 'Cardano Network')}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, color: ADA_COLORS.primary }}>${adaPrice.toFixed(4)}</span>
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>ADA</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>{totalAccounts}</div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('cardano.network.addresses', 'Addresses')}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600, color: ADA_COLORS.success }}>~4.5%</div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('cardano.network.stakingApy', 'Staking APY')}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: isLoading ? ADA_COLORS.warning : ADA_COLORS.success, boxShadow: `0 0 10px ${isLoading ? ADA_COLORS.warning : ADA_COLORS.success}`, margin: '0 auto 4px' }} />
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{isLoading ? t('cardano.network.syncing', 'Syncing...') : t('cardano.network.connected', 'Connected')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatsOverview({ totalBalance, totalBalanceUsd, totalStaked, totalStakedUsd, totalWithdrawable, totalWithdrawableUsd, totalRewards, totalRewardsUsd, totalAssets, stakingCount }: any) {
  const { t } = useTranslation();
  const stats = [
    { label: t('cardano.stats.balance', 'Total Balance'), value: `${totalBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })} ₳`, subvalue: `$${totalBalanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: ADA_COLORS.primary, icon: <Icons.Wallet size={16} /> },
    { label: t('cardano.stats.staked', 'Staked'), value: `${totalStaked.toLocaleString(undefined, { maximumFractionDigits: 2 })} ₳`, subvalue: `$${totalStakedUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: ADA_COLORS.accent, icon: <Icons.Lock size={16} /> },
    { label: t('cardano.stats.withdrawable', 'Withdrawable'), value: `${totalWithdrawable.toLocaleString(undefined, { maximumFractionDigits: 4 })} ₳`, subvalue: `$${totalWithdrawableUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: ADA_COLORS.warning, icon: <Icons.Clock size={16} /> },
    { label: t('cardano.stats.rewards', 'Total Rewards'), value: `${totalRewards.toLocaleString(undefined, { maximumFractionDigits: 2 })} ₳`, subvalue: `${stakingCount} staking`, color: ADA_COLORS.success, icon: <Icons.TrendingUp size={16} /> },
  ];
  return (
    <div style={s.statsGrid}>
      {stats.map((stat, idx) => (
        <div key={idx} style={s.statCard(stat.color)}>
          <div style={{ color: stat.color, marginBottom: '8px' }}>{stat.icon}</div>
          <div style={{ ...s.statValue, color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{stat.subvalue}</div>
          <div style={s.statLabel}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

function AccountCard({ wallet, onRemove }: { wallet: CardanoWalletData; onRemove: () => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ ...s.card, background: 'rgba(0, 51, 173, 0.08)', border: '1px solid rgba(0, 51, 173, 0.2)', marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: ADA_COLORS.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icons.Cardano size={20} color="white" />
          </div>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, marginBottom: '2px' }}>{wallet.address.slice(0, 12)}...{wallet.address.slice(-8)}</div>
            {wallet.isStaking && wallet.poolTicker && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', background: 'rgba(16, 185, 129, 0.15)', color: ADA_COLORS.success, borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>
                <Icons.Zap size={10} /> Staking to [{wallet.poolTicker}]
              </div>
            )}
          </div>
        </div>
        <button onClick={onRemove} style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
          <Icons.Trash2 size={12} />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('cardano.account.balance', 'Balance')}</div>
          <div style={{ fontSize: '16px', fontWeight: 600 }}>{wallet.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} ₳</div>
          <div style={{ fontSize: '11px', color: ADA_COLORS.success }}>${wallet.balanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('cardano.account.staked', 'Staked')}</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: ADA_COLORS.accent }}>{wallet.stakedAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} ₳</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('cardano.account.rewards', 'Rewards')}</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: ADA_COLORS.success }}>{wallet.withdrawableRewards.toLocaleString(undefined, { maximumFractionDigits: 4 })} ₳</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('cardano.account.assets', 'Assets')}</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>{wallet.assets.length} tokens</div>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <NetworkMonitorEmptyState
      icon={<Icons.Cardano size={40} color="white" />}
      title={t('cardano.empty.title', 'Welcome to Cardano Monitor')}
      description={t(
        'cardano.empty.description',
        'Track your Cardano addresses, monitor staking rewards, view native asset holdings, and analyze transaction history.',
      )}
      colors={ADA_COLORS}
    />
  );
}

export function CardanoMonitor() {
  const { t } = useTranslation();
  const toast = useToast();
  const { wallets: accounts, setWallets: setAccounts } = useCardanoMonitorWallets();
  const [newAccountInput, setNewAccountInput] = useState('');
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const { wallets, isLoading, error, refetch, adaPrice, totalBalance, totalBalanceUsd, totalStaked, totalStakedUsd, totalWithdrawable, totalWithdrawableUsd, totalRewards, totalRewardsUsd, totalAssets, stakingCount } = useCardanoWallets(accounts);

  const handleAddAccount = () => {
    const trimmedAddress = newAccountInput.trim();
    if (!trimmedAddress) { toast(t('cardano.toast.enterAddress', 'Please enter an address'), 'warning', 3000); return; }
    if (!isValidCardanoAddress(trimmedAddress)) { toast(t('cardano.toast.invalidFormat', 'Invalid Cardano address format'), 'error', 4000); return; }
    if (accounts.includes(trimmedAddress)) { toast(t('cardano.toast.alreadyAdded', 'This address is already added'), 'warning', 3000); return; }
    setAccounts([...accounts, trimmedAddress]);
    setNewAccountInput('');
    setShowAddAccount(false);
    toast(t('cardano.toast.addressAdded', 'Address added successfully'), 'success', 3000);
  };

  const handleRemoveAccount = (address: string) => {
    if (confirm(t('cardano.confirm.removeAddress', `Remove address?`))) {
      setAccounts(accounts.filter(a => a !== address));
      toast(t('cardano.toast.addressRemoved', 'Address removed'), 'info', 3000);
    }
  };

  const isValidInput = newAccountInput.trim() && isValidCardanoAddress(newAccountInput.trim());
  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: t('cardano.tabs.overview', 'Overview'), icon: <Icons.BarChart size={16} /> },
    { id: 'staking', label: t('cardano.tabs.staking', 'Staking'), icon: <Icons.Award size={16} /> },
    { id: 'transactions', label: t('cardano.tabs.transactions', 'Transactions'), icon: <Icons.ArrowRightLeft size={16} /> },
    { id: 'assets', label: t('cardano.tabs.assets', 'Assets'), icon: <Icons.Coins size={16} /> },
  ];

  const allTransactions = wallets.flatMap(w => w.recentTransactions.map(tx => ({ ...tx, walletAddress: w.address }))).sort((a, b) => b.block_time - a.block_time);
  const assetMap = new Map<string, { asset: string; name: string; ticker: string | null; quantity: number; decimals: number }>();
  for (const wallet of wallets) {
    for (const asset of wallet.assets) {
      const existing = assetMap.get(asset.asset);
      if (existing) { existing.quantity += asset.quantity; }
      else { assetMap.set(asset.asset, { ...asset }); }
    }
  }
  const aggregatedAssets = Array.from(assetMap.values());

  return (
    <div style={s.page}>
      <NetworkMonitorPageHeader
        icon={<Icons.Cardano size={36} color={ADA_COLORS.primary} />}
        title="Cardano Monitor"
        subtitle={t('cardano.subtitle', 'Monitor your Cardano addresses and staking rewards')}
        primaryAction={
          showAddAccount
            ? {
                label: t('common.cancel', 'Cancel'),
                icon: <Icons.X size={16} />,
                onClick: () => setShowAddAccount(false),
                variant: 'secondary',
              }
            : {
                label: t('cardano.addAddress', 'Add Address'),
                icon: <Icons.Plus size={16} />,
                onClick: () => setShowAddAccount(true),
              }
        }
        refreshAction={
          accounts.length > 0
            ? {
                label: t('common.refresh', 'Refresh'),
                icon: <Icons.RefreshCw size={16} />,
                onClick: () => refetch(),
              }
            : undefined
        }
        colors={ADA_COLORS}
      />

      {showAddAccount && (
        <NetworkAddressInputForm
          value={newAccountInput}
          onChange={setNewAccountInput}
          onSubmit={handleAddAccount}
          submitLabel={t('common.add', 'Add')}
          placeholder={t('cardano.placeholder', 'Enter Cardano address (addr1...)')}
          isValid={newAccountInput.trim() ? Boolean(isValidInput) : null}
          colors={ADA_COLORS}
        />
      )}

      <NetworkStatsBanner adaPrice={adaPrice} totalAccounts={accounts.length} isLoading={isLoading} />

      {/* V2.K.7 — ADA supply breakdown (reserve depletion + treasury
          on-chain + cap 45 Md). Fed by CoinGecko, refresh every 5 min. */}
      <AdaSupplyBreakdown />

      {/* Blockchain Health Metrics */}
      <CardanoHealthMetrics />

      {error && <div style={{ ...s.card, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}><div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444' }}><Icons.AlertTriangle size={20} /><span>{t('cardano.error', 'Error loading data')}: {(error as Error)?.message}</span></div></div>}

      {accounts.length > 0 ? (
        <>
          <StatsOverview totalBalance={totalBalance} totalBalanceUsd={totalBalanceUsd} totalStaked={totalStaked} totalStakedUsd={totalStakedUsd} totalWithdrawable={totalWithdrawable} totalWithdrawableUsd={totalWithdrawableUsd} totalRewards={totalRewards} totalRewardsUsd={totalRewardsUsd} totalAssets={totalAssets} stakingCount={stakingCount} />
          <MonitorTabs<TabType>
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            colors={ADA_COLORS}
            ariaLabel="Cardano monitor tabs"
          />

          {activeTab === 'overview' && (
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><Icons.Wallet size={18} />{t('cardano.addresses', 'Addresses')} ({wallets.length})</h2>
              {isLoading ? <div style={{ textAlign: 'center', padding: '40px' }}><Icons.Activity size={32} style={{ opacity: 0.3 }} /></div> : wallets.map(wallet => <AccountCard key={wallet.address} wallet={wallet} onRemove={() => handleRemoveAccount(wallet.address)} />)}
            </div>
          )}

          {activeTab === 'staking' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>{t('cardano.staking.title', 'Staking Details')}</h3>
              <ResponsiveTable<CardanoWalletData>
                data={wallets}
                keyExtractor={(w) => w.address}
                columns={[
                  {
                    key: 'address',
                    header: 'Address',
                    isTitle: true,
                    render: (w) => (
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {w.address.slice(0, 12)}…
                      </span>
                    ),
                  },
                  {
                    key: 'pool',
                    header: 'Pool',
                    align: 'center',
                    render: (w) => w.poolTicker ? (
                      <span style={{ padding: '4px 10px', background: 'rgba(16, 185, 129, 0.15)', color: ADA_COLORS.success, borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                        [{w.poolTicker}]
                      </span>
                    ) : (
                      <span style={{ color: 'var(--muted)', fontSize: '11px' }}>Not staking</span>
                    ),
                  },
                  {
                    key: 'staked',
                    header: 'Staked',
                    align: 'right',
                    render: (w) => `${w.stakedAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} ₳`,
                  },
                  {
                    key: 'rewards',
                    header: 'Rewards',
                    align: 'right',
                    render: (w) => (
                      <span style={{ color: ADA_COLORS.success }}>
                        {w.withdrawableRewards.toLocaleString(undefined, { maximumFractionDigits: 4 })} ₳
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          )}

          {activeTab === 'transactions' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>{t('cardano.transactions.title', 'Recent Transactions')}</h3>
              <ResponsiveTable
                data={allTransactions.slice(0, 50)}
                keyExtractor={(tx, idx) => `${tx.hash}-${idx}`}
                emptyState={
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>
                    {t('cardano.transactions.empty', 'No transactions found')}
                  </div>
                }
                columns={[
                  {
                    key: 'date',
                    header: 'Date',
                    isSubtitle: true,
                    render: (tx) => cardanoAPI.formatTimestamp(tx.block_time).toLocaleDateString(),
                  },
                  {
                    key: 'hash',
                    header: 'Hash',
                    isTitle: true,
                    render: (tx) => (
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--muted)' }}>
                        {tx.hash.slice(0, 16)}…
                      </span>
                    ),
                  },
                  {
                    key: 'fee',
                    header: 'Fee',
                    align: 'right',
                    render: (tx) => (
                      <span style={{ color: 'var(--muted)' }}>
                        {(Number(tx.fees) / 1e6).toFixed(6)} ₳
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          )}

          {activeTab === 'assets' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>{t('cardano.assets.title', 'Native Assets')} ({aggregatedAssets.length})</h3>
              {aggregatedAssets.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
                  {aggregatedAssets.map(asset => (
                    <div key={asset.asset} style={{ ...s.card, marginBottom: 0, background: 'rgba(51, 102, 255, 0.08)', border: '1px solid rgba(51, 102, 255, 0.2)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: '16px', fontWeight: 600, color: ADA_COLORS.accent }}>{asset.ticker || asset.name || 'Unknown'}</div>
                          <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{asset.name}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '16px', fontWeight: 600 }}>{asset.quantity.toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div style={{ ...s.card, textAlign: 'center', color: 'var(--muted)' }}>{t('cardano.assets.empty', 'No assets found')}</div>}
            </div>
          )}
        </>
      ) : <EmptyState />}

      <div style={{ marginTop: '30px', padding: '16px 20px', background: 'rgba(0, 51, 173, 0.08)', borderRadius: '12px', border: '1px solid rgba(0, 51, 173, 0.15)', fontSize: '12px', color: 'var(--muted)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}><Icons.Info size={14} style={{ color: ADA_COLORS.primary }} /><strong style={{ color: 'var(--text)' }}>{t('cardano.info.title', 'About Cardano Monitor')}</strong></div>
        <p style={{ lineHeight: 1.6 }}>{t('cardano.info.description', 'This page uses the Blockfrost API to fetch account data. No wallet connection required.')}</p>
      </div>
    </div>
  );
}
